"use strict";
// General utilities for a map
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.canConvertToClusterMap = canConvertToClusterMap;
exports.canConvertToMarkersMap = canConvertToMarkersMap;
exports.convertToClusterMap = convertToClusterMap;
exports.convertToMarkersMap = convertToMarkersMap;
exports.getFilterableTables = getFilterableTables;
exports.getCompiledFilters = getCompiledFilters;
exports.getTranslatableStrings = getTranslatableStrings;
exports.getSimpleHoverOverData = getSimpleHoverOverData;
const lodash_1 = __importStar(require("lodash"));
const expressions_1 = require("@mwater/expressions");
const LayerFactory_1 = __importDefault(require("./LayerFactory"));
// Check if can convert to a cluster map. Only maps containing marker views can be
function canConvertToClusterMap(design) {
    return lodash_1.default.any(design.layerViews, (lv) => lv.type === "Markers");
}
// Check if can convert to a markers map. Only maps containing cluster views can be
function canConvertToMarkersMap(design) {
    return lodash_1.default.any(design.layerViews, (lv) => lv.type === "Cluster");
}
// Convert to a cluster map
function convertToClusterMap(design) {
    const layerViews = lodash_1.default.map(design.layerViews, (lv) => {
        if (lv.type !== "Markers") {
            return lv;
        }
        lv = lodash_1.default.cloneDeep(lv);
        // Set type and design
        lv.type = "Cluster";
        lv.design = {
            table: lv.design.table,
            axes: { geometry: lv.design.axes != null ? lv.design.axes.geometry : undefined },
            filter: lv.design.filter,
            fillColor: lv.design.color,
            minZoom: lv.design.minZoom,
            maxZoom: lv.design.maxZoom
        };
        return lv;
    });
    return lodash_1.default.extend({}, design, { layerViews });
}
// Convert to a markers map
function convertToMarkersMap(design) {
    const layerViews = lodash_1.default.map(design.layerViews, (lv) => {
        if (lv.type !== "Cluster") {
            return lv;
        }
        lv = lodash_1.default.cloneDeep(lv);
        // Set type and design
        lv.type = "Markers";
        lv.design = {
            table: lv.design.table,
            axes: { geometry: lv.design.axes != null ? lv.design.axes.geometry : undefined },
            filter: lv.design.filter,
            color: lv.design.fillColor,
            minZoom: lv.design.minZoom,
            maxZoom: lv.design.maxZoom
        };
        return lv;
    });
    return lodash_1.default.extend({}, design, { layerViews });
}
// Get ids of filterable tables
function getFilterableTables(design, schema) {
    let filterableTables = [];
    for (let layerView of design.layerViews) {
        // Create layer
        const layer = LayerFactory_1.default.createLayer(layerView.type);
        // Get filterable tables
        filterableTables = lodash_1.default.uniq(filterableTables.concat(layer.getFilterableTables(layerView.design, schema)));
    }
    // Remove non-existant tables
    filterableTables = lodash_1.default.filter(filterableTables, (table) => schema.getTable(table));
    return filterableTables;
}
// Compile map filters with global filters
function getCompiledFilters(design, schema, filterableTables) {
    const exprCompiler = new expressions_1.ExprCompiler(schema);
    const exprCleaner = new expressions_1.ExprCleaner(schema);
    const exprUtils = new expressions_1.ExprUtils(schema);
    const compiledFilters = [];
    // Compile filters to JsonQL expected by layers
    for (const table in design.filters || {}) {
        let expr = design.filters[table];
        // Clean expr
        expr = exprCleaner.cleanExpr(expr, { table });
        const jsonql = exprCompiler.compileExpr({ expr, tableAlias: "{alias}" });
        if (jsonql) {
            compiledFilters.push({ table, jsonql });
        }
    }
    // Add global filters
    for (let filter of design.globalFilters || []) {
        for (const table of filterableTables) {
            // Check if exists and is correct type
            const column = schema.getColumn(table, filter.columnId);
            if (!column) {
                continue;
            }
            const columnExpr = { type: "field", table, column: column.id };
            if (exprUtils.getExprType(columnExpr) !== filter.columnType) {
                continue;
            }
            // Create expr
            let expr = { type: "op", op: filter.op, table, exprs: [columnExpr].concat(filter.exprs) };
            // Clean expr
            expr = exprCleaner.cleanExpr(expr, { table });
            const jsonql = exprCompiler.compileExpr({ expr, tableAlias: "{alias}" });
            if (jsonql) {
                compiledFilters.push({ table, jsonql });
            }
        }
    }
    return compiledFilters;
}
/**
 * Get a list of translatable strings in the map design
 */
function getTranslatableStrings(design, schema) {
    const strings = [];
    // Iterate through each layer view in the design
    for (const layerView of design.layerViews) {
        // Name of the layer is translatable
        strings.push(layerView.name);
        // Create the layer using the LayerFactory
        const layer = LayerFactory_1.default.createLayer(layerView.type);
        // Get the translatable strings from the layer design
        const layerStrings = layer.getTranslatableStrings(layerView.design, schema);
        strings.push(...layerStrings);
    }
    // Remove duplicates
    return lodash_1.default.uniq(strings);
}
/**
 * Convenience function to get hover over data for a map given an id and a list of hover over items
 */
async function getSimpleHoverOverData(options) {
    const { id, table, filters, schema, dataSource, hoverOverItems } = options;
    const exprCompiler = new expressions_1.ExprCompiler(schema);
    const query = {
        type: "query",
        selects: [],
        from: exprCompiler.compileTable(table, "main"),
        limit: 1
    };
    for (const item of hoverOverItems) {
        if (item.value) {
            query.selects.push({
                type: "select",
                expr: exprCompiler.compileExpr({ expr: item.value, tableAlias: "main" }),
                alias: item.id
            });
        }
    }
    if (filters) {
        let whereClauses = filters.filter(f => f.table === table).map(f => (0, expressions_1.injectTableAlias)(f.jsonql, "main"));
        // Add id filter
        if (id != null) {
            whereClauses.push({
                type: "op",
                op: "=",
                exprs: [
                    exprCompiler.compileExpr({ expr: { type: "id", table }, tableAlias: "main" }),
                    { type: "literal", value: id }
                ]
            });
        }
        whereClauses = (0, lodash_1.compact)(whereClauses);
        // Wrap if multiple
        if (whereClauses.length > 1) {
            query.where = { type: "op", op: "and", exprs: whereClauses };
        }
        else {
            query.where = whereClauses[0];
        }
    }
    const rows = await dataSource.performQuery(query);
    return rows?.[0] ?? {};
}
